import client from "services/ngage/api-client";
import {Sponsorship, SponsorshipData} from "types/Sponsorship";
import deserializeSponsorship from "services/ngage/deserializers/sponsorship";
import {queryString} from "services/string";
import DataResource from "types/DataResource";
import SponsorType from "types/SponsorType";
import {snakeKeys} from "../array";
import SponsorableType from "../../types/SponsorableType";

type SponsorshipFilters = {
    sponsorableType?: SponsorableType
    sponsorableId?: number
    sponsorType?: SponsorType
    sponsorId?: number
    search?: string | null
    sort?: string | null
    page?: number
    perPage?: number
}

/**
 * Get all sponsorships (paginated)
 */
const all = async ({sponsorId, sponsorType, page = 1, perPage = 100}: SponsorshipFilters) => {
    const query = queryString({sponsor_id: sponsorId, sponsor_type: sponsorType, page, per_page: perPage})
    const response = await client.get<DataResource<Sponsorship> | null>(`/sponsorships?${query}`)

    if (response.data) {
        response.data.data = response.data.data.map(deserializeSponsorship)
    }

    return response.data
}

/**
 * Get all sponsorships that match the given filters (paginated)
 */
const filtered = async (filters: SponsorshipFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))
    const response = await client.get<DataResource<Sponsorship> | null>('/sponsorships', {params})

    if (response.data) {
        response.data.data = response.data.data.map(deserializeSponsorship)
    }

    return response.data
}

/**
 * Get a single sponsorship
 *
 * @param {number} id
 */
const get = async (id: number) => {
    return deserializeSponsorship(
        (await client.get<Sponsorship>(`/sponsorships/${id}`)).data
    )
}

/**
 * Store a new sponsorship 
 *
 * @param data
 */
const store = async (data: SponsorshipData) => {
    return deserializeSponsorship(
        (await client.post<Sponsorship>('/sponsorships', data)).data
    )
}

/**
 * Update an existing sponsorship 
 *
 * @param {number} id
 * @param data
 */
const update = async (id: number, data: SponsorshipData) => {
    return deserializeSponsorship(
        (await client.put<Sponsorship>(`/sponsorships/${id}`, data)).data
    )
}

/**
 * Permanently delete a sponsorship
 *
 * @param {number} id
 */
const destroy = async (id: number) => {
    return (await client.delete<null>(`/sponsorships/${id}`)).status === 204
}

const sponsorships = {
    all,
    filtered,
    get,
    store,
    update,
    destroy
}

export default sponsorships;